import { z } from 'zod';
import { isoDateStringSchema } from './common.js';

/**
 * Custom fields: what a module's list asks for, as something a shop owns rather than something
 * we decided.
 *
 * Two different things live in one list, on purpose:
 *
 *   - the BUILT-IN fields, whose keys are fixed for the whole product because features read
 *     them by name — search and duplicate detection read the phone number, booking reads the
 *     name. A shop owns exactly two things about these: what they are CALLED, and whether they
 *     are shown at all;
 *   - the CUSTOM fields a shop adds, which we know nothing about beyond their type. These are
 *     stored against each record by key and rendered generically.
 *
 * A KEY is never editable — not for a built-in, where it is a canonical constant, and not for a
 * custom field, where it is derived once from the label the shop first typed. Renaming writes
 * `label` and nothing else, so a field renamed after two years of use still finds its values.
 *
 * Adding a module is an entry in `CUSTOM_FIELD_MODULE_DEFINITIONS` and nothing else. The entry
 * carries everything the service and the screens need to know about a module — its built-ins,
 * its cap, where its rows live, which permissions gate it, what its audit trail is called, and
 * where the web finds its built-in labels.
 */

/* -------------------------------------------------------------------------------------------
 * The modules that own a field list
 * ---------------------------------------------------------------------------------------- */

export const CUSTOM_FIELD_MODULES = ['customers'] as const;
export type CustomFieldModule = (typeof CUSTOM_FIELD_MODULES)[number];
export const customFieldModuleSchema = z.enum(CUSTOM_FIELD_MODULES);

/* -------------------------------------------------------------------------------------------
 * Built-in fields
 * ---------------------------------------------------------------------------------------- */

export interface CustomFieldBuiltin {
  readonly key: string;
  /**
   * The English default. The WEB resolves labels through i18n (the module's `i18nPrefix`) so
   * they translate; this literal is for server-side consumers — CSV export, audit summaries —
   * that have no `t()`.
   */
  readonly defaultLabel: string;
  /** True for a field that cannot be hidden: the rest of the product reads it by key. */
  readonly essential: boolean;
  readonly showInTableByDefault: boolean;
  /**
   * False for a field the form does not ask for. `createdAt` is stamped by the database — worth a
   * column and worth renaming, but not something anybody types.
   */
  readonly inForm: boolean;
  /** True for a field that says WHO the record is. */
  readonly identity: boolean;
}

/* -------------------------------------------------------------------------------------------
 * Custom field types
 * ---------------------------------------------------------------------------------------- */

/**
 * What a shop can add. Deliberately a closed list: each type is one input, one way of being
 * stored, and one way of being read back, and "anything you like" is a column nobody can report
 * on.
 *
 * `money` is rupees on the way in and out, and paise in the database — the same convention as
 * every other amount in this product.
 */
export const CUSTOM_FIELD_TYPES = [
  'text',
  'long_text',
  'select',
  'number',
  'money',
  'date',
  'boolean',
] as const;
export type CustomFieldType = (typeof CUSTOM_FIELD_TYPES)[number];
export const customFieldTypeSchema = z.enum(CUSTOM_FIELD_TYPES);

export const MAX_CUSTOM_FIELD_LABEL_LENGTH = 40;
export const MAX_CUSTOM_FIELD_HELPER_LENGTH = 200;
export const MAX_CUSTOM_FIELD_OPTIONS = 40;
export const MAX_CUSTOM_FIELD_OPTION_LENGTH = 60;
/** A cap, not a target. Past this the form is a questionnaire and the counter stops filling it. */
export const MAX_CUSTOM_FIELDS = 30;
export const MAX_CUSTOM_FIELD_TEXT_LENGTH = 500;
export const MAX_CUSTOM_FIELD_LONG_TEXT_LENGTH = 2000;

export const customFieldKeySchema = z
  .string()
  .trim()
  .min(1)
  .max(40)
  .regex(/^[a-z][a-zA-Z0-9_]*$/, 'A field key is lower snake_case');

export const customFieldLabelSchema = z
  .string()
  .trim()
  .min(1, 'Give the field a name')
  .max(MAX_CUSTOM_FIELD_LABEL_LENGTH)
  .transform((value) => value.replace(/\s+/g, ' '));

/* -------------------------------------------------------------------------------------------
 * The stored row, and the resolved view every screen reads
 * ---------------------------------------------------------------------------------------- */

/**
 * One row of a shop's configuration. `label: null` means "use the default" — stored that way
 * rather than as a copy of the default, so a later change to the default (or a translation)
 * still reaches a shop that never really renamed it.
 */
export const customFieldSettingSchema = z.object({
  key: customFieldKeySchema,
  origin: z.enum(['builtin', 'custom']),
  label: z.string().nullable(),
  hidden: z.boolean(),
  showInTable: z.boolean(),
  sortOrder: z.number().int(),
  type: customFieldTypeSchema.nullable(),
  options: z.array(z.string()),
  helperText: z.string().nullable(),
  required: z.boolean(),
});
export type CustomFieldSetting = z.infer<typeof customFieldSettingSchema>;

/** What the screens read: the stored row with its defaults and rules resolved. */
export const resolvedCustomFieldSchema = customFieldSettingSchema.extend({
  defaultLabel: z.string(),
  canHide: z.boolean(),
  renamed: z.boolean(),
  inForm: z.boolean(),
  identity: z.boolean(),
});
export type ResolvedCustomField = z.infer<typeof resolvedCustomFieldSchema>;

export const customFieldConfigurationSchema = z.object({
  fields: z.array(resolvedCustomFieldSchema),
});
export type CustomFieldConfiguration = z.infer<typeof customFieldConfigurationSchema>;

/* -------------------------------------------------------------------------------------------
 * Editing the list
 * ---------------------------------------------------------------------------------------- */

const optionsSchema = z
  .array(z.string().trim().min(1).max(MAX_CUSTOM_FIELD_OPTION_LENGTH))
  .max(MAX_CUSTOM_FIELD_OPTIONS);

/** Adding one. The KEY is absent on purpose — the server derives it from the label, once. */
export const addCustomFieldSchema = z
  .object({
    label: customFieldLabelSchema,
    type: customFieldTypeSchema,
    options: optionsSchema.optional(),
    helperText: z.string().trim().max(MAX_CUSTOM_FIELD_HELPER_LENGTH).nullish(),
    required: z.boolean().optional(),
    showInTable: z.boolean().optional(),
  })
  .refine((field) => field.type !== 'select' || (field.options?.length ?? 0) > 0, {
    message: 'A dropdown needs at least one choice',
    path: ['options'],
  });
export type AddCustomFieldInput = z.input<typeof addCustomFieldSchema>;

/** Editing one. `key` is not accepted, so a rename can only ever be a label. */
export const updateCustomFieldSchema = z
  .object({
    label: customFieldLabelSchema.nullish(),
    hidden: z.boolean().optional(),
    showInTable: z.boolean().optional(),
    options: optionsSchema.optional(),
    helperText: z.string().trim().max(MAX_CUSTOM_FIELD_HELPER_LENGTH).nullish(),
    required: z.boolean().optional(),
  })
  .refine((patch) => Object.keys(patch).length > 0, { message: 'Nothing to update' });
export type UpdateCustomFieldInput = z.input<typeof updateCustomFieldSchema>;

export const reorderCustomFieldsSchema = z.object({
  order: z.array(customFieldKeySchema).min(1),
});
export type ReorderCustomFieldsInput = z.input<typeof reorderCustomFieldsSchema>;

/* -------------------------------------------------------------------------------------------
 * Values
 * ---------------------------------------------------------------------------------------- */

/**
 * One stored value. Four typed slots rather than one untyped one, so a number sorts as a number
 * and a date as a date; which slot a type uses is `CUSTOM_FIELD_SLOT`.
 */
export const customFieldValueSchema = z.object({
  fieldKey: customFieldKeySchema,
  text: z.string().nullable(),
  number: z.number().finite().nullable(),
  date: isoDateStringSchema.nullable(),
  checked: z.boolean().nullable(),
});
export type CustomFieldValue = z.infer<typeof customFieldValueSchema>;

export const customFieldValueInputSchema = z.object({
  fieldKey: customFieldKeySchema,
  text: z.string().trim().max(MAX_CUSTOM_FIELD_LONG_TEXT_LENGTH).nullish(),
  number: z.number().finite().nullish(),
  date: isoDateStringSchema.nullish(),
  checked: z.boolean().nullish(),
});
export type CustomFieldValueInput = z.input<typeof customFieldValueInputSchema>;

/** True when a value says nothing — which is stored as no row at all. */
export function isBlankCustomFieldValue(value: {
  text?: string | null;
  number?: number | null;
  date?: string | null;
  checked?: boolean | null;
}): boolean {
  return (
    (value.text ?? '') === '' &&
    (value.number ?? null) === null &&
    (value.date ?? null) === null &&
    (value.checked ?? null) === null
  );
}

/** Which slot of a stored value each type writes to and reads from. */
export const CUSTOM_FIELD_SLOT: Readonly<
  Record<CustomFieldType, 'text' | 'number' | 'date' | 'checked'>
> = Object.freeze({
  text: 'text',
  long_text: 'text',
  select: 'text',
  number: 'number',
  money: 'number',
  date: 'date',
  boolean: 'checked',
});

/* -------------------------------------------------------------------------------------------
 * The customer book's built-in fields
 * ---------------------------------------------------------------------------------------- */

/**
 * The customer book's own columns.
 *
 * `name` and `primaryPhone` are essential AND identity: search, duplicate detection and order
 * booking all read them by key, so a customer book without them is one the rest of the product
 * cannot work with.
 */
export const CUSTOMER_BUILTIN_FIELDS: readonly CustomFieldBuiltin[] = Object.freeze([
  { key: 'name', defaultLabel: 'Name', essential: true, showInTableByDefault: true, inForm: true, identity: true }, // prettier-ignore
  { key: 'primaryPhone', defaultLabel: 'Mobile', essential: true, showInTableByDefault: true, inForm: true, identity: true }, // prettier-ignore
  // NOTE: `alternatePhone` is deliberately absent — see `RETIRED_CUSTOMER_BUILTIN_FIELD_KEYS`.
  { key: 'kind', defaultLabel: 'Type', essential: false, showInTableByDefault: false, inForm: true, identity: false }, // prettier-ignore
  { key: 'gstin', defaultLabel: 'GSTIN', essential: false, showInTableByDefault: false, inForm: true, identity: true }, // prettier-ignore
  { key: 'email', defaultLabel: 'Email', essential: false, showInTableByDefault: false, inForm: true, identity: true }, // prettier-ignore
  { key: 'address', defaultLabel: 'Address', essential: false, showInTableByDefault: false, inForm: true, identity: false }, // prettier-ignore
  { key: 'tags', defaultLabel: 'Tags', essential: false, showInTableByDefault: true, inForm: true, identity: false }, // prettier-ignore
  { key: 'source', defaultLabel: 'Source', essential: false, showInTableByDefault: false, inForm: true, identity: false }, // prettier-ignore
  { key: 'notes', defaultLabel: 'Notes', essential: false, showInTableByDefault: false, inForm: true, identity: false }, // prettier-ignore
  { key: 'createdAt', defaultLabel: 'Added', essential: false, showInTableByDefault: true, inForm: false, identity: false }, // prettier-ignore
]);

/* -------------------------------------------------------------------------------------------
 * The registry
 * ---------------------------------------------------------------------------------------- */

/** Where a module's rows live on the shop settings document. */
export type CustomFieldStorage = 'moduleCustomFields';

export interface CustomFieldModuleDefinition {
  readonly module: CustomFieldModule;
  /** The fields the product itself puts in this module's list. */
  readonly builtinFields: readonly CustomFieldBuiltin[];
  /**
   * Keys that used to be in `builtinFields` and are not any more. A shop that predates the
   * removal still has a stored row for one; it is ignored on read and must not be mistaken for
   * an invented built-in on write.
   */
  readonly retiredBuiltinKeys: readonly string[];
  /** How many fields of its own a shop may add to this module. */
  readonly maxCustomFields: number;
  /** Which path on the shop settings document holds this module's rows. */
  readonly storage: CustomFieldStorage;
  /** The permission pair that gates reading and editing this module's configuration. */
  readonly readPermission: string;
  readonly writePermission: string;
  /** `<prefix>.added` / `.updated` / `.reordered` / `.deleted` in the audit trail. */
  readonly auditPrefix: 'custom_fields';
  /** Where the web resolves a built-in's translated label: `<prefix><key>`. */
  readonly i18nPrefix: string;
}

/**
 * A customer used to carry a second number, and a shop configured before it was dropped still
 * has a stored row for it. The resolver rebuilds the built-ins from the canonical list, so the
 * row is ignored on read; this list is what stops the validator calling it an invented field
 * and refusing the shop's next unrelated edit.
 */
export const RETIRED_CUSTOMER_BUILTIN_FIELD_KEYS: readonly string[] = Object.freeze([
  'alternatePhone',
]);

export const CUSTOM_FIELD_MODULE_DEFINITIONS: Readonly<
  Record<CustomFieldModule, CustomFieldModuleDefinition>
> = Object.freeze({
  customers: {
    module: 'customers',
    builtinFields: CUSTOMER_BUILTIN_FIELDS,
    retiredBuiltinKeys: RETIRED_CUSTOMER_BUILTIN_FIELD_KEYS,
    maxCustomFields: MAX_CUSTOM_FIELDS,
    storage: 'moduleCustomFields',
    readPermission: 'custom_fields.read',
    writePermission: 'custom_fields.write',
    auditPrefix: 'custom_fields',
    i18nPrefix: 'customers.column.',
  },
} satisfies Record<CustomFieldModule, CustomFieldModuleDefinition>);

/** The built-in descriptor for `key` in `module`, if the product ships one. */
export function customFieldBuiltin(
  module: CustomFieldModule,
  key: string,
): CustomFieldBuiltin | undefined {
  return CUSTOM_FIELD_MODULE_DEFINITIONS[module].builtinFields.find((field) => field.key === key);
}

/** True when `key` names one of `module`'s built-in fields. */
export function isCustomFieldBuiltinKey(module: CustomFieldModule, key: string): boolean {
  return customFieldBuiltin(module, key) !== undefined;
}

/** True when `key` named one of `module`'s built-in fields in an earlier release. */
export function isRetiredCustomFieldBuiltinKey(module: CustomFieldModule, key: string): boolean {
  return CUSTOM_FIELD_MODULE_DEFINITIONS[module].retiredBuiltinKeys.includes(key);
}
